import { isCloudBrand } from './BrandContext';
import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { authApi, platformApi } from '../services/api';
import { setAuthMode, isBadgeSession } from '../utils/authMode';

const CLIENT_FORBIDDEN_SECTIONS = ['storage', 'placement', 'scanning', 'picking'];

// «Только явно» — как на сервере (access_sections.EXPLICIT_SECTIONS): деньги, удаление,
// списание, отмена и бывшие «только admin» экраны (разбор прав 05.10). Эти права не
// даёт ни «полный доступ» (permissions = null), ни роль администратора клиента —
// только администратор системы или отмеченная галочка.
export const EXPLICIT_SECTIONS = [
  'shipment_close', 'shipment_discrepancy', 'shipment_edit',
  'stock_writeoff', 'delete_data', 'stock_log',
  'marking_admin', 'fbs_recount', 'fbs_manage',
  'activity', 'companies', 'billing', 'services_tz', 'consumables', 'finance',
  'finance_post', 'finance_settings', 'finance_taxes', 'finance_payroll',
  'equipment', 'notifications', 'kb_editor',
  // CRM: расширенные права — только галочкой (как на сервере)
  'crm_all', 'crm_assign', 'crm_delete', 'crm_delete_forever', 'crm_settings',
  'crm_reports', 'crm_contracts', 'crm_export', 'crm_audit',
  'marketing', 'marketing_costs', 'marketing_settings', 'marketing_tg',
  'tasks_dispatch',
];

// Тариф склада (облако Н3): модули для меню и маршрутов. У Full-Fix сервер отвечает
// modules: null — всё включено, как раньше. Кэш — чтобы у склада облака закрытые
// пункты не мелькали до ответа сервера (localStorage у каждого адреса склада свой).
const PLAN_KEY = 'wms_plan';
function readPlan() {
  try {
    return JSON.parse(localStorage.getItem(PLAN_KEY) || 'null');
  } catch {
    return null;
  }
}

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [plan, setPlan] = useState(readPlan);

  useEffect(() => {
    const token = localStorage.getItem('wms_token');
    const savedUser = localStorage.getItem('wms_user');
    if (token && savedUser) {
      try {
        const parsed = JSON.parse(savedUser);
        setUser(parsed);
        setCompanies(parsed.companies || []);
      } catch {
        localStorage.removeItem('wms_token');
        localStorage.removeItem('wms_user');
      }
    }
    setLoading(false);
  }, []);

  // Права и компании — с сервера, а не только из localStorage: раньше изменение
  // доступа доходило до интерфейса, лишь когда человек сам выходил и входил
  // (аудит №145). Серверные проверки и так читают пользователя из базы — здесь
  // догоняем меню и кнопки: при открытии и раз в 10 минут. Ошибку молча
  // пропускаем — 401 разберёт перехватчик api.js, сбой сети не должен выкидывать.
  const userId = user?.id;
  useEffect(() => {
    if (!userId) return undefined;
    let cancelled = false;
    const sync = () => {
      if (!localStorage.getItem('wms_token')) return;
      authApi.me().then(res => {
        const fresh = res.data;
        if (cancelled || !fresh) return;
        if (fresh.id !== userId) {
          // Токен уже другого человека (вошли в соседней вкладке или на терминале
          // сменили сотрудника) — показывать прежнего с его кнопками нельзя (аудит №4).
          localStorage.setItem('wms_user', JSON.stringify(fresh));
          window.location.reload();
          return;
        }
        const prevRaw = localStorage.getItem('wms_user');
        const nextRaw = JSON.stringify(fresh);
        if (prevRaw === nextRaw) return;
        localStorage.setItem('wms_user', nextRaw);
        setUser(fresh);
        setCompanies(fresh.companies || []);
      }).catch(() => {});
    };
    sync();
    const t = setInterval(sync, 10 * 60 * 1000);
    return () => { cancelled = true; clearInterval(t); };
  }, [userId]);

  // Тариф склада — при входе и раз в 10 минут (владелец платформы сменил тариф —
  // меню догонит без перезахода). Сбой — остаёмся на прежнем: сервер всё равно
  // закрывает выключенные модули сам (403).
  // Перечитать тариф сейчас — «Каталог модулей» после сохранения: меню и маршруты
  // сразу видят включённое и выключенное
  const refreshPlan = () => platformApi.plan().then((res) => {
    if (!res?.data) return;
    setPlan(res.data);
    try { localStorage.setItem(PLAN_KEY, JSON.stringify(res.data)); } catch { /* приватный режим */ }
  }).catch(() => {});

  useEffect(() => {
    if (!userId) return undefined;
    let cancelled = false;
    const load = () => {
      if (!localStorage.getItem('wms_token')) return;
      platformApi.plan().then((res) => {
        if (cancelled || !res?.data) return;
        setPlan(res.data);
        try { localStorage.setItem(PLAN_KEY, JSON.stringify(res.data)); } catch { /* приватный режим */ }
      }).catch(() => {});
    };
    load();
    const t = setInterval(load, 10 * 60 * 1000);
    return () => { cancelled = true; clearInterval(t); };
  }, [userId]);

  // Смена входа в другой вкладке: браузер присылает событие storage. Вышли там —
  // перезагружаем (попадём на вход); вошёл другой человек — перезагружаем под ним;
  // тот же человек (обновился токен или права) — только обновляем данные (аудит №4).
  const userRef = useRef(user);
  userRef.current = user;
  useEffect(() => {
    const onStorage = (e) => {
      if (e.storageArea && e.storageArea !== window.localStorage) return;
      if (e.key !== null && e.key !== 'wms_token' && e.key !== 'wms_user') return;
      const curId = userRef.current?.id;
      const token = localStorage.getItem('wms_token');
      let next = null;
      try { next = JSON.parse(localStorage.getItem('wms_user') || 'null'); } catch { next = null; }
      if (!token || !next) {
        if (curId) window.location.reload();
        return;
      }
      if (next.id !== curId) {
        window.location.reload();
        return;
      }
      if (e.key === 'wms_user') {
        setUser(next);
        setCompanies(next.companies || []);
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const login = async (username, password) => {
    const response = await authApi.login(username, password);
    const { access_token, refresh_token, user: userData } = response.data;
    localStorage.setItem('wms_token', access_token);
    if (refresh_token) localStorage.setItem('wms_refresh_token', refresh_token);
    localStorage.setItem('wms_user', JSON.stringify(userData));
    setAuthMode('password');
    setUser(userData);
    setCompanies(userData.companies || []);
    return userData;
  };

  // Вход по бейджу: сотрудник сканирует наклейку на терминале склада.
  // refresh-токена нет намеренно — сессия живёт ровно смену и не продлевается
  // молча: ушёл со смены, пришёл другой человек — нужно сканировать заново.
  //
  // Тем же вызовом работает «Сменить сотрудника»: прежняя сессия закрывается
  // только ПОСЛЕ успешного скана — промах не выкидывает того, кто сейчас работает.
  // Долгий вход по паролю (refresh на 90 дней) гасим на сервере: иначе ключ
  // общей учётки оставался бы живым в браузере терминала.
  // Прежний бейдж-токен этого устройства уходит в запросе заголовком (интерсептор
  // api.js) — сервер гасит его после успешного скана; ТСД того человека не трогает.
  const loginByBadge = async (code) => {
    const prevRefresh = localStorage.getItem('wms_refresh_token');
    const response = await authApi.badgeLogin(code);
    const { access_token, user: userData } = response.data;
    if (prevRefresh) authApi.logout(prevRefresh).catch(() => {});
    localStorage.removeItem('wms_refresh_token');
    localStorage.setItem('wms_token', access_token);
    localStorage.setItem('wms_user', JSON.stringify(userData));
    setAuthMode('badge');
    setUser(userData);
    setCompanies(userData.companies || []);
    return userData;
  };

  const logout = () => {
    const refreshToken = localStorage.getItem('wms_refresh_token');
    if (refreshToken) {
      authApi.logout(refreshToken).catch(() => {});
    }
    localStorage.removeItem('wms_token');
    localStorage.removeItem('wms_refresh_token');
    localStorage.removeItem('wms_user');
    localStorage.removeItem(PLAN_KEY);
    setUser(null);
    setCompanies([]);
    setPlan(null);
  };

  const updateUser = (userData) => {
    setUser(userData);
    setCompanies(userData.companies || []);
    localStorage.setItem('wms_user', JSON.stringify(userData));
  };

  const isAdmin = user?.role === 'ADMIN' && !user?.company_id;
  const isCompanyAdmin = user?.role === 'ADMIN' && !!user?.company_id || user?.role === 'COMPANY_ADMIN';
  const isCompanyUser = !!user?.company_id;
  // INTERNAL_STAFF — сотрудник склада MPFit (не клиент). Используется
  // для гейтинга UI бота: виджет привязки и страница /admin/notifications.
  const isInternalStaff = !!user && !user.company_id && (user.role === 'ADMIN' || user.role === 'WORKER');
  const companyId = user?.company_id || null;
  const isMultiCompany = companies.length > 1;
  const permissions = user?.permissions || null; // null = полный доступ
  // Складские разделы клиенту закрыты при любых правах — как на сервере
  // (auth.CLIENT_FORBIDDEN_SECTIONS, 24.09.2026): раньше администратор клиента по
  // прямой ссылке открывал хранение, размещение и сканирование склада.
  const hasPermission = (section) => {
    if (user?.company_id && CLIENT_FORBIDDEN_SECTIONS.includes(section)) return false;
    if (EXPLICIT_SECTIONS.includes(section)) {
      return isAdmin || (isInternalStaff && !!permissions && permissions.includes(section));
    }
    return isAdmin || isCompanyAdmin || !permissions || permissions.includes(section);
  };
  // Сотрудник склада с правом на раздел (администратор системы — всегда): для кнопок
  // внутри экранов, которые раньше видел только админ (разбор прав 05.10)
  const can = (section) => isAdmin || (isInternalStaff && hasPermission(section));
  // Финансы собственника — как has_finance_access на сервере: ADMIN без ограничений
  // разделов или с 'finance', WORKER — только с явным 'finance'.
  const canFinance = isInternalStaff && (
    user.role === 'ADMIN' ? (!permissions || permissions.includes('finance'))
                          : !!permissions && permissions.includes('finance'));
  // Роли внутри «Денег» (21.10): finance_post — проводить, finance_settings — настройки,
  // finance_taxes — налоги, finance_payroll — зарплата. Правило то же, что у canFinance.
  const canFin = (section) => canFinance && (
    user.role === 'ADMIN' ? (!permissions || permissions.includes(section))
                          : !!permissions && permissions.includes(section));

  // Модули тарифа (облако Н3): null — всё включено (Full-Fix или тариф ещё не пришёл)
  const modules = plan && plan.modules ? plan.modules : null;
  // Облако: пока тариф не пришёл, разделы с модулем считаем закрытыми — иначе в меню на
  // секунду мелькали выключенные «Смена», «Сверки», «Хвосты», а клик уводил в сторону
  // (обход 29.09). Guard такие адреса в это время не отбивает, а ждёт тариф.
  const planPending = !!user && !plan && isCloudBrand();
  const moduleOn = (name) => !name || (planPending ? false : (!modules || modules[name] !== false));
  // Кабинет платформы — только владелец Full-Fix (сервер решает, фронт лишь прячет пункт)
  const isPlatformOwner = isAdmin && !!plan?.platform_owner;

  return (
    <AuthContext.Provider value={{
      user, loading, login, loginByBadge, logout, updateUser, isBadgeSession,
      isAdmin, isCompanyAdmin, isCompanyUser, isInternalStaff, companyId,
      companies, isMultiCompany, permissions, hasPermission, can, canFinance, canFin,
      plan, modules, moduleOn, planPending, isPlatformOwner, refreshPlan,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
